import { useState, useEffect } from "react";
import { useGetAdsOne } from "../functions/homeAds.ts";

const CORPORATE_THEMES = [
    {
        background: 'radial-gradient(circle at 100% 0%, #F0F4FF 0%, #E6EFFF 100%)',
        border: '#B4C7F0',
        accent: '#6B90DC',
        ribbon: '#4A6BAB',
        buttonFrom: '#6B90DC',
        buttonTo: '#4A6BAB',
        textGradient: 'linear-gradient(to right, #4A6BAB, #6B90DC)'
    },
    {
        background: 'radial-gradient(circle at 100% 0%, #F5FDFF 0%, #E5F7FF 100%)',
        border: '#9ED0E6',
        accent: '#5AA4C4',
        ribbon: '#3A7E9A',
        buttonFrom: '#5AA4C4',
        buttonTo: '#3A7E9A',
        textGradient: 'linear-gradient(to right, #3A7E9A, #5AA4C4)'
    },
    {
        background: 'radial-gradient(circle at 100% 0%, #F8F5FF 0%, #F0EBFF 100%)',
        border: '#C6B4E6',
        accent: '#8E7DC7',
        ribbon: '#6754A3',
        buttonFrom: '#8E7DC7',
        buttonTo: '#6754A3',
        textGradient: 'linear-gradient(to right, #6754A3, #8E7DC7)'
    },
    {
        background: 'radial-gradient(circle at 100% 0%, #FFF5F5 0%, #FFEBEB 100%)',
        border: '#E6A3A3',
        accent: '#D46B6B',
        ribbon: '#B14747',
        buttonFrom: '#D46B6B',
        buttonTo: '#B14747',
        textGradient: 'linear-gradient(to right, #B14747, #D46B6B)'
    }
];

const getThemeByAdId = (adId: string) => {
    const hash = adId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return CORPORATE_THEMES[hash % CORPORATE_THEMES.length];
};

const FloatingAd = ({ page = 1, limit = 1 }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [canClose, setCanClose] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(CORPORATE_THEMES[0]);
    const { ads, isLoading, isError } = useGetAdsOne(page, limit);

    useEffect(() => {
        const timer = setTimeout(() => setCanClose(true), 5000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (ads?.[0]?._id) {
            setCurrentTheme(getThemeByAdId(ads[0]._id));
        }
    }, [ads]);

    if (!isVisible || isError || !ads) return null;

    const adsArray = Array.isArray(ads) ? ads : [ads];
    const ad = adsArray[0];

    const handleClick = () => {
        window.open(ad?.link, "_blank");
    };

    return (
        <div className="fixed bottom-6 right-6 shadow-xl rounded-2xl p-5 w-80 z-[20000]
        animate-float hover:shadow-lg transition-all duration-500 hover:-translate-y-1
        backdrop-blur-sm border"
             style={{
                 background: currentTheme.background,
                 borderColor: currentTheme.border,
                 boxShadow: `0 15px 30px ${currentTheme.border}20`
             }}
        >
            {/* Skeleton Loader */}
            {isLoading ? (
                <div className="space-y-4 animate-pulse">
                    <div className="h-7 bg-gray-200/30 rounded w-3/4 mx-auto" />
                    <div className="h-4 bg-gray-200/30 rounded w-5/6 mx-auto" />
                    <div className="h-48 bg-gray-200/30 rounded-xl" />
                    <div className="h-12 bg-gray-200/30 rounded-lg" />
                </div>
            ) : (
                <>
                    {/* Close button */}
                    <button
                        className={`absolute -top-3 -right-3 bg-white hover:bg-[${currentTheme.accent}] text-[${currentTheme.accent}] hover:text-white 
                rounded-full w-7 h-7 flex items-center justify-center shadow-md transition-all duration-300
                ${!canClose && 'opacity-0 cursor-not-allowed'}`}
                        onClick={() => canClose && setIsVisible(false)}
                        aria-label="Close ad"
                        style={{
                            backgroundColor: canClose ? 'white' : 'transparent',
                            color: currentTheme.accent
                        }}
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>

                    {/* Ribbon */}
                    <div className="absolute -top-4 left-4 px-3 py-1 rounded-full
              text-xs font-semibold shadow-sm animate-bounce-in"
                         style={{
                             backgroundColor: currentTheme.ribbon,
                             color: 'white'
                         }}>
                        Limited Time ⏳
                    </div>

                    <div className="space-y-4 mt-4">
                        {/* Text content */}
                        <div className="text-center space-y-3">
                            <h3 className="font-bold text-2xl tracking-tight"
                                style={{
                                    background: currentTheme.textGradient,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}>
                                {ad.title}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed font-medium">
                                {ad.description}
                            </p>
                        </div>

                        {/* Image container */}
                        <div className="relative group overflow-hidden rounded-xl border-2 transition-all duration-500"
                             style={{
                                 borderColor: currentTheme.border + '50',
                                 borderWidth: '2px'
                             }}>
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10 z-10"/>
                            <img
                                src={ad.image}
                                alt="Advertisement"
                                className="w-full h-48 object-contain transform transition-transform
                    duration-500 group-hover:scale-105"
                            />
                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20
                  to-transparent -translate-x-full group-hover:translate-x-full
                  transition-transform duration-1000"/>
                        </div>

                        {/* CTA Button */}
                        <button
                            onClick={handleClick}
                            className="w-full text-white font-semibold py-3 rounded-lg shadow-md
                  hover:shadow-lg transition-all duration-300 flex items-center justify-center
                  space-x-2 relative overflow-hidden"
                            style={{
                                background: `linear-gradient(to right, ${currentTheme.buttonFrom}, ${currentTheme.buttonTo})`
                            }}
                        >
                            <div className="absolute inset-0 bg-white/10 -translate-x-full hover:translate-x-0
                  transition-transform duration-700"/>
                            <span className="relative">{ad.buttonText || "View Offer"}</span>
                            <svg className="w-5 h-5 fill-current relative" viewBox="0 0 24 24">
                                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                            </svg>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default FloatingAd;